import { computed } from 'vue'
import { autreEstChoisie, texteAutre } from '../../../shared/question-options.js'
import { BESOIN_COMMUN_SECTIONS, validateBesoinCommun } from '../questionnaires/besoin-commun-schema.js'

/**
 * CE QUI MANQUE À L'ANALYSE DU BESOIN, SECTION PAR SECTION.
 *
 * Chaque manque est un objet `{ section, field, label, message }` :
 *  — `section` : la section dont il bloque la confirmation ;
 *  — `field`   : le champ qui passe en rouge (`data-invalid` à l'écran) ;
 *  — `label`   : ce que compte le pied de la fenêtre ;
 *  — `message` : ce qui s'écrit sous le champ.
 *
 * UNE SEULE LISTE pour tout ce qui en dépend — le pied, les pastilles du plan,
 * le bouton final et le refus de confirmer une section. Avant, une table de
 * libellés rattachait après coup chaque manque à sa section, et tout libellé
 * inconnu tombait dans « Validation des modalités ».
 *
 * AJOUTER UN CHAMP OBLIGATOIRE (retour Figma : « la règle doit valoir pour les
 * cases obligatoires qu'on ajoutera ») :
 *  — dans une section commune : `required` dans besoin-commun-sections.js ;
 *  — dans un questionnaire de qualification : la case « obligatoire » de la
 *    question, au back-office ;
 *  — ailleurs : une liste de manques de plus dans `extraMissing`.
 * Rien d'autre à brancher.
 *
 * @param {Object} deps
 * @param {import('vue').Ref<Object|null>} deps.quote - Demande (null au chargement).
 * @param {Object} deps.besoinQuestionnaire - Réponses des sections communes.
 * @param {import('vue').Ref<Array>} deps.specificQuestionnaires - Questionnaires de qualification affichés.
 * @param {import('vue').Ref<Object>} deps.answers - Réponses aux questions, par id.
 * @param {Function} deps.questionsPosees - Questions réellement posées d'un questionnaire.
 * @param {import('vue').Ref<number|string>} deps.nombreGroupes - Nombre de groupes retenu.
 * @param {Array<import('vue').Ref<Array>>} [deps.extraMissing] - Autres manques, déjà formés.
 */
export function useBesoinMissing(deps) {
  const missingItems = computed(() => {
    if (!deps.quote.value) return []
    return [
      ...communMissing(deps.besoinQuestionnaire),
      ...groupesMissing(deps.nombreGroupes.value),
      ...questionnairesMissing(deps),
      ...(deps.extraMissing || []).flatMap((list) => list.value || [])
    ]
  })

  const missingFields = computed(() => missingItems.value.map((m) => m.label))

  /** Nombre de manques par section : la pastille du plan, le refus de confirmer. */
  const missingBySection = computed(() => {
    const counts = {}
    for (const { section } of missingItems.value) counts[section] = (counts[section] || 0) + 1
    return counts
  })

  /** Message d'erreur par champ, pour une section. */
  function errorsFor(section) {
    const errors = {}
    for (const m of missingItems.value) {
      if (m.section === section) errors[m.field] = m.message
    }
    return errors
  }

  return { missingItems, missingFields, missingBySection, errorsFor }
}

/** Champ commun (ou sa précision « autre ») → section et libellé du manque. */
const COMMUN_FIELD_INDEX = (() => {
  const index = new Map()
  for (const section of BESOIN_COMMUN_SECTIONS) {
    for (const field of section.fields) {
      index.set(field.key, { section: section.key, label: field.missingLabel || field.label })
      if (field.autreKey) {
        index.set(field.autreKey, { section: section.key, label: field.autreLabel || `${field.label} — précisez` })
      }
    }
  }
  return index
})()

/** Les sections communes portent leurs règles dans le schéma partagé avec l'étape 2. */
function communMissing(besoin) {
  const { errors } = validateBesoinCommun(besoin)
  return Object.entries(errors).map(([field, message]) => {
    const known = COMMUN_FIELD_INDEX.get(field)
    if (!known) {
      // Inatteignable tant que l'index et la validation lisent le même schéma :
      // si ça arrive, le schéma a divergé — on le dit au lieu de classer au hasard.
      console.error(`[analyse du besoin] champ commun sans section connue : ${field}`)
    }
    const { section, label } = known || { section: BESOIN_COMMUN_SECTIONS[0].key, label: field }
    return { section, field, label, message }
  })
}

function groupesMissing(nombreGroupes) {
  if (nombreGroupes && Number(nombreGroupes) >= 1) return []
  return [{
    section: 'formation',
    field: 'nombre_groupes',
    label: 'Nombre de groupe',
    message: 'Indiquez au moins un groupe.'
  }]
}

/**
 * UNIQUEMENT LES QUESTIONS RÉELLEMENT POSÉES. Réclamer une question qu'une
 * branche fermée a fait disparaître bloquait la confirmation sur un champ
 * introuvable à l'écran.
 */
function questionnairesMissing({ specificQuestionnaires, answers, questionsPosees }) {
  const missing = []
  for (const questionnaire of specificQuestionnaires.value || []) {
    for (const question of questionsPosees(questionnaire)) {
      if (!exigeeDansLeTunnel(question)) continue
      if (hasAnswer(question, answers.value[question.id])) continue
      missing.push({
        // Posées dans « Formation » (retour Figma du 2026-09-11) : c'est
        // cette section qu'une réponse manquante empêche de confirmer.
        section: 'formation',
        field: `q-${question.id}`,
        label: `${questionnaire.name} — ${question.label}`,
        message: 'Réponse obligatoire.'
      })
    }
  }
  return missing
}

/**
 * Les questions à réponse unique : une option à choisir, pas une phrase à écrire.
 */
const TYPES_A_REPONSE_UNIQUE = new Set(['radio', 'select'])

/**
 * UNE QUESTION À RÉPONSE UNIQUE EST TOUJOURS EXIGÉE DANS LE TUNNEL (retour
 * Figma du 2026-10-05 : « Idem pour les questions spécifiques de la
 * formation » — on pouvait continuer sans y répondre). Le commercial a le
 * client au téléphone : choisir une option ne lui coûte rien, et un devis
 * chiffré sans la réponse repart en rappel.
 *
 * PAS LES CASES À COCHER : « cochez ce qui s'applique » admet le vide comme
 * réponse — « Connexion wifi » n'a qu'une case, « Exercice d'évacuation » pas
 * d'option « aucun ». Les exiger forcerait une réponse fausse, que le client
 * et le formateur retrouveraient ensuite. Elles, comme les champs à écrire,
 * restent exigées seulement quand le catalogue les dit obligatoires.
 *
 * PROPRE AU TUNNEL : le drapeau `required` du catalogue n'est pas modifié, car
 * il décide aussi de ce que le client doit remplir pour signer son
 * questionnaire d'organisation.
 */
export function exigeeDansLeTunnel(question) {
  if (question.type === 'info') return false
  return Boolean(question.required) || TYPES_A_REPONSE_UNIQUE.has(question.type)
}

/** « Autre » cochée sans rien derrière n'est pas une réponse : c'est le texte qui répond. */
function hasAnswer(question, a) {
  const given = Boolean(a) && (
    (a.answer != null && String(a.answer).trim() !== '') ||
    (Array.isArray(a.selectedOptions) && a.selectedOptions.length > 0)
  )
  return given && !(autreEstChoisie(question, a) && !texteAutre(a))
}
